import { CommentData } from '../types';

export interface CommentTreeNode extends CommentData {
  children: CommentTreeNode[];
  depth: number;
}

/**
 * Builds a tree from a flat list of comments using parentCommentId.
 * Returns only root-level nodes; nested replies are in `children`.
 */
export function buildCommentTree(comments: CommentData[]): CommentTreeNode[] {
  const map = new Map<string, CommentTreeNode>();
  const roots: CommentTreeNode[] = [];

  for (const c of comments) {
    map.set(c.id, { ...c, children: [], depth: 0 });
  }

  for (const node of map.values()) {
    if (node.parentCommentId && map.has(node.parentCommentId)) {
      const parent = map.get(node.parentCommentId)!;
      node.depth = parent.depth + 1;
      parent.children.push(node);
    } else if (!node.parentCommentId) {
      roots.push(node);
    }
  }

  return roots;
}

/**
 * Flattens a tree into a sorted array suitable for rendering,
 * with `depth` preserved on each node. Sorts children by createdAt ascending.
 */
export function flattenTree(roots: CommentTreeNode[]): CommentTreeNode[] {
  const result: CommentTreeNode[] = [];

  function walk(nodes: CommentTreeNode[]) {
    const sorted = [...nodes].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    for (const node of sorted) {
      result.push(node);
      if (node.children.length > 0) {
        walk(node.children);
      }
    }
  }

  walk(roots);
  return result;
}

/** Max visual indentation depth */
export const MAX_INDENT_DEPTH = 4;
